import { useState } from 'react';
import { MoreVertical } from 'lucide-react';
import type { JobFileEntry } from '@jobos/shared';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useJobFileDownload } from './useJobFileDownload';

export interface JobFileActions {
  onView?: (file: JobFileEntry) => void;
  onEdit?: (file: JobFileEntry) => void;
  onMove?: (file: JobFileEntry) => void;
  onDelete?: (file: JobFileEntry) => void;
}

interface JobFileActionsMenuProps extends JobFileActions {
  jobId: string;
  file: JobFileEntry;
  className?: string;
}

/** A menu entry whose handler hasn't landed yet stays visible but inert. */
function ActionItem({
  label,
  onSelect,
  className,
}: {
  label: string;
  onSelect?: () => void;
  className?: string;
}) {
  return (
    <DropdownMenuItem
      onClick={onSelect}
      data-disabled={onSelect ? undefined : ''}
      className={className}
    >
      {label}
    </DropdownMenuItem>
  );
}

/**
 * The per-file actions menu, shared by the grid card and the list row so the
 * two views can't drift into offering different things.
 *
 * Note the items are plain divs with no `menuitem` role — a pre-existing gap in
 * components/ui/dropdown-menu.tsx, which is Popover-based and shared with every
 * other menu in the app. Out of scope here.
 */
export function JobFileActionsMenu({
  jobId,
  file,
  onView,
  onEdit,
  onMove,
  onDelete,
  className,
}: JobFileActionsMenuProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const download = useJobFileDownload(jobId);

  const handleDownload = async () => {
    setMenuOpen(false);
    await download(file.id);
  };

  const select = (handler?: (file: JobFileEntry) => void) =>
    handler
      ? () => {
          setMenuOpen(false);
          handler(file);
        }
      : undefined;

  return (
    <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className={className}
          aria-label={`Actions for ${file.fileName}`}
        >
          <MoreVertical className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <ActionItem label="View" onSelect={select(onView)} />
        <ActionItem label="Download" onSelect={handleDownload} />
        <ActionItem label="Edit" onSelect={select(onEdit)} />
        <ActionItem label="Move" onSelect={select(onMove)} />
        <ActionItem
          label="Delete"
          onSelect={select(onDelete)}
          className="text-red-600 hover:bg-red-50 focus:bg-red-50"
        />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
